import type { ReactNode } from 'react';
import { STATUS_TONES } from '@t3/shared';
import type { TicketStatus, StatusTone } from '@t3/shared';

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={[
        'rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-sm',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </div>
  );
}

const TONE_CLASSES: Record<StatusTone, string> = {
  // Soft fills that read clearly against light surfaces. Border + text use
  // the same hue so the pill keeps its identity even when the surface
  // behind it (Card, table row, page bg) changes.
  green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  purple: 'border-violet-200 bg-violet-50 text-violet-800',
  red: 'border-rose-200 bg-rose-50 text-rose-800',
  grey: 'border-[var(--color-border)] bg-white text-[var(--color-muted)]',
};

export function StatusBadge({
  status,
  children,
}: {
  status?: TicketStatus;
  children: ReactNode;
}): React.ReactElement {
  const tone: StatusTone = status ? STATUS_TONES[status] ?? 'grey' : 'grey';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${TONE_CLASSES[tone]}`}
    >
      {children}
    </span>
  );
}

export function TypeBadge({ type }: { type: string }): React.ReactElement {
  const colors: Record<string, string> = {
    escalation: 'bg-[var(--color-t3-blue)] text-white',
    project: 'bg-[var(--color-t3-purple)] text-white',
    quote: 'bg-amber-500 text-white',
    chat: 'bg-emerald-600 text-white',
  };
  const cls = colors[type] ?? 'bg-[var(--color-muted)] text-white';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${cls}`}>
      {type}
    </span>
  );
}
